import { useEffect, useMemo, useRef, useState } from "react"
import {
  FaSignOutAlt,
  FaAngleLeft,
  FaAngleRight,
  FaChevronDown,
  FaChevronRight,
  FaCog,
  FaBriefcase,
  FaClipboardList,
  FaCheck,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaPlus,
} from "react-icons/fa"
import { IconType } from "react-icons"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { SmartLogo } from "@/modules/shared/ui/components/custom/SmartLogo"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { useSelector } from "react-redux"
import {
  selectCurrentOrganizationId,
  selectCurrentOrganization,
} from "@/modules/organizations/store/organizationSlice"
import { OrganizationStatus } from "@/modules/organizations/enums/organization-status"
import { Avatar, AvatarFallback } from "@/modules/shared/ui/components/avatar"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { Organization } from "@/modules/organizations/entities/Organization"
import { toast } from "sonner"
import {
  buildNavSections,
  NavItem,
  NavSection,
} from "@/modules/layout/config/nav-config"
import { AppRole } from "@/modules/auth/constants/app-role"
import { useEmployeeOnboarding } from "@/modules/employee/employee/hooks/employeeHook"

// ── Organisation switcher ──────────────────────────────────────────────────

interface OrgSwitcherProps {
  collapsed: boolean
}

function OrgSwitcher({ collapsed }: OrgSwitcherProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { organizations, currentOrganization, selectOrganization } =
    useOrganizationsService()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSwitch = (org: Organization) => {
    try {
      selectOrganization(org)
      navigate("/dashboard")
    } catch (err) {
      toast.error(`Failed to switch organisation: ${err}`)
    } finally {
      setOpen(false)
    }
  }

  const orgName = currentOrganization?.name ?? "Select Organisation"
  const orgInitial = orgName[0]?.toUpperCase() ?? "O"

  // No organisations — replace the switcher with a straight "Create organisation" CTA.
  if (organizations.length === 0) {
    return (
      <div className="relative px-2 py-2 border-b border-sidebar-border">
        <button
          onClick={() => navigate("/select-organisation")}
          title={collapsed ? "Create organisation" : undefined}
          className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors text-left text-primary
                      ${collapsed ? "justify-center" : ""}`}
        >
          <div className="h-7 w-7 rounded-md border-2 border-dashed border-primary/40 flex items-center justify-center shrink-0">
            <FaPlus className="h-3 w-3 text-primary" />
          </div>
          {!collapsed && (
            <span className="flex-1 text-sm font-semibold truncate">
              Create organisation
            </span>
          )}
        </button>
      </div>
    )
  }

  return (
    <div
      className="relative px-2 py-2 border-b border-sidebar-border"
      ref={dropdownRef}
    >
      <button
        onClick={() => setOpen((prev) => !prev)}
        title={collapsed ? orgName : undefined}
        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors text-left
                    ${collapsed ? "justify-center" : ""}`}
      >
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold rounded-md">
            {orgInitial}
          </AvatarFallback>
        </Avatar>
        {!collapsed && (
          <>
            <span className="flex-1 text-sm font-semibold text-foreground truncate">
              {orgName}
            </span>
            <FaChevronDown
              className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </>
        )}
      </button>

      {open && (
        <div
          className={`absolute z-50 bg-popover border border-border rounded-xl shadow-lg overflow-hidden
                        ${
                          collapsed
                            ? "left-full ml-2 top-0 w-64"
                            : "left-2 right-2 top-full mt-1"
                        }`}
        >
          <div className="px-4 py-2.5 border-b border-border bg-muted">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Your Organisations
            </p>
          </div>
          <ul className="py-1 max-h-56 overflow-y-auto">
            {organizations.map((org) => {
              const isCurrent = org.id === currentOrganization?.id
              return (
                <li key={org.id}>
                  <button
                    onClick={() => handleSwitch(org)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left
                                            ${isCurrent ? "bg-primary/10" : ""}`}
                  >
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback
                        className={`text-xs font-semibold rounded-md ${
                          isCurrent
                            ? "bg-primary/20 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {org.name?.[0]?.toUpperCase() ?? "O"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${
                          isCurrent ? "text-primary" : "text-foreground"
                        }`}
                      >
                        {org.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {org.plan}
                      </p>
                    </div>
                    {isCurrent && (
                      <FaCheck className="h-3 w-3 text-primary shrink-0" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
          <div className="border-t border-border py-1">
            <button
              onClick={() => {
                setOpen(false)
                navigate("/select-organisation")
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-primary"
            >
              <FaExchangeAlt className="h-3.5 w-3.5 shrink-0" />
              <span className="text-sm font-medium cursor-pointer">
                Switch Organisation
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Employee org context (read-only for single org, switcher for multi-org) ──

function EmployeeOrgContext({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { organizations, currentOrganization, selectOrganization } =
    useOrganizationsService()
  const { onboardingData, loadOnboardingData } = useEmployeeOnboarding()

  const orgName =
    currentOrganization?.name ??
    onboardingData?.organizationName ??
    "Organisation"
  const orgInitial = orgName[0]?.toUpperCase() ?? "O"
  const canSwitch = organizations.length > 1

  useEffect(() => {
    if (!canSwitch) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [canSwitch])

  const handleSwitch = (org: Organization) => {
    try {
      selectOrganization(org)
      loadOnboardingData()
      navigate("/employee/dashboard")
    } catch (err) {
      toast.error(`Failed to switch organisation: ${err}`)
    } finally {
      setOpen(false)
    }
  }

  return (
    <div
      className="relative px-2 py-2 border-b border-sidebar-border"
      ref={dropdownRef}
    >
      <button
        onClick={() => canSwitch && setOpen((prev) => !prev)}
        title={collapsed ? orgName : undefined}
        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg transition-colors text-left
                    ${canSwitch ? "hover:bg-muted cursor-pointer" : "cursor-default"}
                    ${collapsed ? "justify-center" : ""}`}
      >
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="bg-green-100 text-green-700 text-xs font-bold rounded-md">
            {orgInitial}
          </AvatarFallback>
        </Avatar>
        {!collapsed && (
          <>
            <span className="flex-1 text-sm font-semibold text-foreground truncate">
              {orgName}
            </span>
            {canSwitch && (
              <FaChevronDown
                className={`h-3 w-3 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
              />
            )}
          </>
        )}
      </button>

      {canSwitch && open && (
        <div
          className={`absolute z-50 bg-popover border border-border rounded-xl shadow-lg overflow-hidden
                        ${
                          collapsed
                            ? "left-full ml-2 top-0 w-64"
                            : "left-2 right-2 top-full mt-1"
                        }`}
        >
          <div className="px-4 py-2.5 border-b border-border bg-muted">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Your Organisations
            </p>
          </div>
          <ul className="py-1 max-h-56 overflow-y-auto">
            {organizations.map((org) => {
              const isCurrent = org.id === currentOrganization?.id
              return (
                <li key={org.id}>
                  <button
                    onClick={() => handleSwitch(org)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left
                                            ${isCurrent ? "bg-primary/10" : ""}`}
                  >
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarFallback
                        className={`text-xs font-semibold rounded-md ${
                          isCurrent
                            ? "bg-primary/20 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {org.name?.[0]?.toUpperCase() ?? "O"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${isCurrent ? "text-primary" : "text-foreground"}`}
                      >
                        {org.name}
                      </p>
                    </div>
                    {isCurrent && (
                      <FaCheck className="h-3 w-3 text-primary shrink-0" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── Nav item renderer ──────────────────────────────────────────────────────

interface NavItemRendererProps {
  item: NavItem
  depth?: number
  collapsed: boolean
  expandedItems: string[]
  onToggle: (key: string) => void
  activePath: string
}

function NavItemRenderer({
  item,
  depth = 0,
  collapsed,
  expandedItems,
  onToggle,
  activePath,
}: NavItemRendererProps) {
  const Icon = item.icon as IconType
  const paddingLeft = collapsed ? "12px" : `${(depth + 1) * 12}px`
  const itemKey = `${depth}-${item.label}`

  if (item.submenu) {
    const isExpanded = expandedItems.includes(itemKey)
    const isActive = item.submenu.some((c) => c.path === activePath)

    return (
      <div className="space-y-0.5">
        <button
          onClick={() => !collapsed && onToggle(itemKey)}
          style={{ paddingLeft }}
          title={collapsed ? item.label : undefined}
          className={`w-full flex items-center justify-between pr-3 py-2.5 rounded-lg transition-all
                        ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
        >
          <div className="flex items-center gap-3">
            <Icon
              className={`text-base shrink-0 ${isActive ? "text-primary" : "text-muted-foreground"}`}
            />
            {!collapsed && (
              <span className="text-sm font-medium">{item.label}</span>
            )}
          </div>
          {!collapsed && (
            <span className="text-muted-foreground">
              {isExpanded ? (
                <FaChevronDown className="text-xs" />
              ) : (
                <FaChevronRight className="text-xs" />
              )}
            </span>
          )}
        </button>

        {!collapsed && isExpanded && (
          <div className="space-y-0.5">
            {item.submenu.map((child) => (
              <NavItemRenderer
                key={child.path ?? child.label}
                item={child}
                depth={depth + 1}
                collapsed={collapsed}
                expandedItems={expandedItems}
                onToggle={onToggle}
                activePath={activePath}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (item.path) {
    const isActive = activePath === item.path
    return (
      <Link
        to={item.path}
        style={{ paddingLeft }}
        title={collapsed ? item.label : undefined}
        className={`flex items-center gap-3 pr-3 py-2.5 rounded-lg transition-all
                    ${isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
      >
        <Icon
          className={`text-base shrink-0 ${isActive ? "text-primary" : "text-muted-foreground"}`}
        />
        {!collapsed && <span className="text-sm">{item.label}</span>}
      </Link>
    )
  }

  return null
}

// ── Restricted-organisation notice ─────────────────────────────────────────

function RestrictedOrgNotice({ collapsed }: { collapsed: boolean }) {
  const message = "This organisation's subscription is inactive."

  if (collapsed) {
    return (
      <div className="flex justify-center px-2 py-3" title={message}>
        <FaExclamationTriangle className="text-base text-destructive shrink-0" />
      </div>
    )
  }

  return (
    <div className="mx-2 my-2 p-3 rounded-lg bg-destructive/10 space-y-2">
      <div className="flex items-start gap-2">
        <FaExclamationTriangle className="text-destructive text-sm mt-0.5 shrink-0" />
        <p className="text-xs text-destructive font-medium">{message}</p>
      </div>
      <Link
        to="/billing"
        className="block text-xs font-semibold text-primary hover:underline"
      >
        Go to Billing &amp; Subscriptions
      </Link>
    </div>
  )
}

// ── Section title icon map ─────────────────────────────────────────────────

const SECTION_ICON_MAP: Record<string, IconType> = {
  Recruitment: FaBriefcase,
  Onboarding: FaClipboardList,
  Settings: FaCog,
}

// ── Main sidebar ───────────────────────────────────────────────────────────

export default function AuthenticatedSidebar() {
  const [collapsed, setCollapsed] = useState(false)
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const location = useLocation()
  const { logout, hasRole, roles } = useAuth()
  const currentOrganizationId = useSelector(selectCurrentOrganizationId)
  const currentOrg = useSelector(selectCurrentOrganization)

  const allRoles = useMemo(() => {
    const merged = [...roles] as AppRole[]
    const orgRole = currentOrg?.userRole as AppRole
    if (orgRole && !merged.includes(orgRole)) {
      merged.push(orgRole)
    }
    return merged
  }, [roles, currentOrg?.userRole])

  const isOrgRestricted =
    currentOrg?.status === OrganizationStatus.Inactive ||
    currentOrg?.status === OrganizationStatus.Pending

  const navSections: NavSection[] = isOrgRestricted
    ? []
    : buildNavSections({
        currentPlan: currentOrg?.plan,
        currentOrgId: currentOrganizationId,
        userRoles: allRoles,
      })

  const toggleItem = (key: string) =>
    setExpandedItems((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    )

  return (
    <div
      className={`bg-sidebar border-r border-sidebar-border h-screen flex flex-col transition-all duration-300 shrink-0
                ${collapsed ? "w-[60px]" : "w-60"}`}
    >
      {/* Logo / collapse toggle */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-sidebar-border">
        <div className="flex-1 flex justify-center">
          <SmartLogo
            size="md"
            className={`${collapsed ? "w-8" : "w-36"} dark:brightness-0 dark:invert`}
          />
        </div>
        <button
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors shrink-0"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <FaAngleRight className="text-sm" />
          ) : (
            <FaAngleLeft className="text-sm" />
          )}
        </button>
      </div>

      {/* Context block: org switcher for org roles, read-only org name for employee-only users.
                An admin/superadmin who also has the Employee role gets the org switcher. */}
      {hasRole(AppRole.OrganizationOwner) ||
      hasRole(AppRole.RegisteredUser) ||
      hasRole(AppRole.TeamMember) ||
      hasRole(AppRole.SuperAdmin) ? (
        <OrgSwitcher collapsed={collapsed} />
      ) : (
        <EmployeeOrgContext collapsed={collapsed} />
      )}

      {/* Nav sections */}
      <nav className="flex-1 px-2 py-3 space-y-4 overflow-y-auto">
        {isOrgRestricted && <RestrictedOrgNotice collapsed={collapsed} />}
        {!isOrgRestricted &&
          navSections.map((section, i) => {
            const SectionIcon =
              section.icon ??
              (section.title ? SECTION_ICON_MAP[section.title] : undefined)
            return (
              <div key={i} className="space-y-0.5">
                {section.title && !collapsed && (
                  <div className="px-3 pb-1 pt-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      {SectionIcon && <SectionIcon className="text-[10px]" />}
                      {section.title}
                    </p>
                  </div>
                )}
                {section.items.map((item) => (
                  <NavItemRenderer
                    key={item.path ?? item.label}
                    item={item}
                    collapsed={collapsed}
                    expandedItems={expandedItems}
                    onToggle={toggleItem}
                    activePath={location.pathname}
                  />
                ))}
              </div>
            )
          })}
      </nav>

      {/* Logout */}
      <div className="px-2 py-3 border-t border-sidebar-border">
        <button
          onClick={logout}
          title={collapsed ? "Sign out" : undefined}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-destructive hover:bg-destructive/10 transition-all w-full cursor-pointer
                        ${collapsed ? "justify-center" : ""}`}
        >
          <FaSignOutAlt className="text-base shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Sign out</span>}
        </button>
      </div>
    </div>
  )
}
